import React from 'react';

import env from '@repo/env';
import * as Sentry from '@sentry/react';
import { Scope } from '@sentry/react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';

import { isCreateImageBitmapSupported } from '../utils/createImageBitmapSupport';

const tracePropagationTargets = [
  new RegExp('^' + process.env.REACT_APP_CMS_API_URL),
  new RegExp('^' + env.apiUrl),
];

const monitoring = {
  _reportingOff(): boolean {
    return env.nodeEnv === 'development' || !process.env.REACT_APP_SENTRY_URL;
  },

  init(): void {
    if (this._reportingOff()) {
      return;
    }
    const integrations = [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
        traceFetch: true,
        traceXHR: true,
      }),
    ];

    // createImageBitmap not supported on old browsers, disabling replays instead of polyfill not to affect performance
    if (isCreateImageBitmapSupported()) {
      integrations.push(
        Sentry.replayIntegration({
          maskAllInputs: true,
          maskAllText: true,
          blockAllMedia: true,
          // enable capturing X-Request-Id headers in replays
          networkDetailAllowUrls: tracePropagationTargets,
          networkRequestHeaders: ['X-Request-Id'],
        })
      );
    }

    Sentry.init({
      dsn: process.env.REACT_APP_SENTRY_URL,
      release:
        process.env.REACT_APP_PROD_RELEASE_NAME ??
        process.env.REACT_APP_SENTRY_RELEASE,
      attachStacktrace: true,
      autoSessionTracking: true,
      beforeSend(event) {
        try {
          let val = event.exception?.values?.[0]?.value;
          if (typeof val === 'object' && val !== null) {
            val = JSON.stringify(val);
          }
          if (val === '{"isTrusted":false}') {
            return null;
          }
        } catch {
          return event;
        }
        return event;
      },
      beforeBreadcrumb(breadcrumb, hint) {
        if (breadcrumb.category === 'ui.click' && hint) {
          const { target } = hint.event;
          if (target.textContent) {
            breadcrumb.message = `Element clicked: ${target.localName} with text content "${target.textContent}" ${target.dataset['testid'] ? `and test-id "${target.dataset['testid']}"` : ''}`;
          }
        }
        return breadcrumb;
      },
      environment: process.env.REACT_APP_SENTRY_ENV,
      replaysSessionSampleRate: 0, // only replay on errors
      replaysOnErrorSampleRate: 1,
      enableTracing: true,
      integrations,
      ignoreErrors: [
        // This comes from embedded chromium in .NET apps,
        // see discussion here: https://forum.sentry.io/t/unhandledrejection-non-error-promise-rejection-captured-with-value/14062/20
        'Object Not Found Matching Id:',
      ],
      tracesSampleRate: 0.01,
      tracePropagationTargets: [
        'localhost',
        // only requests starting with exact urls are traced (regex will be tested verbatim on request url):
        ...tracePropagationTargets,
      ],
    });
  },

  getCurrentScope(): Scope {
    if (this._reportingOff()) {
      return new Scope();
    }
    return Sentry.getCurrentScope();
  },

  setUser(user: Sentry.User): void {
    if (this._reportingOff()) {
      return;
    }

    Sentry.setUser(user);
  },

  setTags: (tags: Record<string, string | number | boolean>): void => {
    // prefix tags with 'kittl.' to avoid collisions with other tags
    const prefixedTags = Object.entries(tags).reduce(
      (acc, [key, value]) => ({
        ...acc,
        [`kittl.${key}`]: value,
      }),
      {}
    );

    if (monitoring._reportingOff()) {
      // eslint-disable-next-line no-console
      console.info('Tags:', prefixedTags);
      return;
    }

    Sentry.setTags(prefixedTags);
  },

  captureException(error: unknown, extraInfo?: unknown): void {
    if (this._reportingOff()) {
      console.error({ error, extraInfo });
    } else {
      extraInfo
        ? Sentry.captureException(error, {
            extra: {
              info: extraInfo,
            },
          })
        : Sentry.captureException(error);
      const replay = Sentry.getReplay();
      if (replay) {
        void replay.flush(); // manually flushing replay, no await
      }
    }
  },

  captureMessage(
    message: string,
    options?: { level: Sentry.SeverityLevel }
  ): void {
    Sentry.captureMessage(message, options);
  },
};

export default monitoring;
